// In-browser mock of the Supabase client surface we use.
//
// When `VITE_SUPABASE_URL` is empty, the real Supabase client is replaced with
// this mock so demo accounts work end-to-end on Prathap's laptop without ever
// touching the cloud. Data lives in localStorage and IndexedDB (image blobs).
//
// The shape only mimics the slice of `@supabase/supabase-js` that the app
// actually calls. New call sites need to extend this file in lockstep.

const LS_KEY = 'np.mock.db';
const LS_SESSION = 'np.mock.session';
const LS_VERSION = 'np.mock.seed_version';
// Bump when seedRifleClub / seedDental / seedRestaurant change so live users
// get fresh demo data on next page load.
const SEED_VERSION = '2026-06-29-base-coa-2210-deductions';

interface MockSession {
  user: { id: string; email: string };
  access_token: 'mock';
  refresh_token: 'mock';
  expires_in: 3600;
  token_type: 'bearer';
}

type Table =
  | 'profiles'
  | 'businesses'
  | 'accounts'
  | 'journal_entries'
  | 'journal_lines'
  | 'receipts'
  | 'invoices'
  | 'invoice_payments'
  | 'inventory_items'
  | 'fixed_assets'
  | 'customers'
  | 'suppliers'
  | 'employees'
  | 'reminders'
  | 'merchants'
  | 'audit_log'
  | 'user_telegram'
  // Healthcare / dental patient management (mirror of 0009_patient_management.sql).
  | 'patients'
  | 'patient_visits'
  | 'patient_prescriptions'
  | 'patient_audit_log';

interface DB {
  users: Array<{ id: string; email: string; password: string }>;
  profiles: any[];
  businesses: any[];
  accounts: any[];
  journal_entries: any[];
  journal_lines: any[];
  receipts: any[];
  invoices: any[];
  invoice_payments: any[];
  inventory_items: any[];
  fixed_assets: any[];
  customers: any[];
  suppliers: any[];
  employees: any[];
  reminders: any[];
  merchants: any[];
  audit_log: any[];
  user_telegram: any[];
  patients: any[];
  patient_visits: any[];
  patient_prescriptions: any[];
  patient_audit_log: any[];
}

function emptyDb(): DB {
  return {
    users: [],
    profiles: [],
    businesses: [],
    accounts: [],
    journal_entries: [],
    journal_lines: [],
    receipts: [],
    invoices: [],
    invoice_payments: [],
    inventory_items: [],
    fixed_assets: [],
    customers: [],
    suppliers: [],
    employees: [],
    reminders: [],
    merchants: [],
    audit_log: [],
    user_telegram: [],
    patients: [],
    patient_visits: [],
    patient_prescriptions: [],
    patient_audit_log: [],
  };
}

function load(): DB {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return emptyDb();
    return { ...emptyDb(), ...JSON.parse(raw) };
  } catch {
    return emptyDb();
  }
}

function save(db: DB) {
  localStorage.setItem(LS_KEY, JSON.stringify(db));
}

// On seed-version bump, wipe stored mock DB + session so users see fresh data.
try {
  const stored = localStorage.getItem(LS_VERSION);
  if (stored !== SEED_VERSION) {
    localStorage.removeItem(LS_KEY);
    localStorage.removeItem(LS_SESSION);
    localStorage.setItem(LS_VERSION, SEED_VERSION);
  }
} catch {
  // localStorage unavailable; carry on with in-memory.
}

let _db = load();
let _session: MockSession | null = (() => {
  try {
    const raw = localStorage.getItem(LS_SESSION);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
})();

const authListeners: Array<(event: string, session: MockSession | null) => void> = [];
function notifyAuth(event: string) {
  for (const fn of authListeners) fn(event, _session);
}

// ============================================================
// Demo data seeder
// ============================================================

function uuid(): string {
  return crypto.randomUUID();
}

function postSimple(
  bizId: string,
  userId: string,
  date: string,
  description: string,
  drAccountId: string,
  crAccountId: string,
  amount: number,
) {
  const eid = uuid();
  _db.journal_entries.push({
    id: eid,
    business_id: bizId,
    entry_date: date,
    reference: description,
    description,
    source: 'manual',
    source_id: null,
    is_posted: true,
    is_locked: false,
    posted_at: new Date().toISOString(),
    created_by: userId,
    created_at: new Date().toISOString(),
  });
  _db.journal_lines.push(
    {
      id: uuid(),
      entry_id: eid,
      account_id: drAccountId,
      debit: amount,
      credit: 0,
      vat_amount: 0,
      vat_rate: 0,
      description,
    },
    {
      id: uuid(),
      entry_id: eid,
      account_id: crAccountId,
      debit: 0,
      credit: amount,
      vat_amount: 0,
      vat_rate: 0,
      description,
    },
  );
}

function ensureAccount(
  bizId: string,
  glCode: string,
  name: string,
  type: 'asset' | 'liability' | 'equity' | 'income' | 'expense',
): string {
  const existing = _db.accounts.find((a) => a.business_id === bizId && a.gl_code === glCode);
  if (existing) return existing.id;
  const id = uuid();
  _db.accounts.push({
    id,
    business_id: bizId,
    gl_code: glCode,
    name,
    type,
    parent_id: null,
    is_active: true,
    is_system: true,
    created_at: new Date().toISOString(),
  });
  return id;
}

// ============================================================
// Customers / suppliers / employees seeder per industry.
// Gives every demo business realistic AR/AP/payroll on day one.
// ============================================================
function seedCommons(args: {
  bizId: string;
  userId: string;
  industry: 'rifle' | 'dental' | 'restaurant';
}) {
  const { bizId, industry } = args;
  const today = new Date();
  const minus = (days: number) => {
    const d = new Date(today);
    d.setDate(d.getDate() - days);
    return d.toISOString().slice(0, 10);
  };

  const customerSets = {
    rifle: [
      { name: 'NRC Defence Force', credit_limit: 80_000, on_hold: false, kind: 'corporate' },
      { name: 'BDF Sports Club', credit_limit: 50_000, on_hold: false, kind: 'corporate' },
      { name: 'Mr Kabelo Modise', credit_limit: 5_000, on_hold: false, kind: 'individual' },
      { name: 'Dr Lerato Pheto', credit_limit: 5_000, on_hold: false, kind: 'individual' },
      { name: 'Mr Tumelo Sebina', credit_limit: 5_000, on_hold: true, kind: 'individual' },
    ],
    dental: [
      { name: 'Bot. Insurance Group', credit_limit: 120_000, on_hold: false, kind: 'corporate' },
      { name: 'Mascom Wireless (staff)', credit_limit: 80_000, on_hold: false, kind: 'corporate' },
      { name: 'Mma Khumalo', credit_limit: 8_000, on_hold: false, kind: 'individual' },
      { name: 'Mr Pheto', credit_limit: 8_000, on_hold: false, kind: 'individual' },
      { name: 'Ms Modise', credit_limit: 8_000, on_hold: true, kind: 'individual' },
    ],
    restaurant: [
      { name: 'BIH Corporate Lunch Club', credit_limit: 60_000, on_hold: false, kind: 'corporate' },
      { name: 'Botswana Open Committee', credit_limit: 40_000, on_hold: false, kind: 'corporate' },
      { name: 'Walk-in (cash only)', credit_limit: 0, on_hold: false, kind: 'walk_in' },
      { name: 'Mr Kgosi', credit_limit: 5_000, on_hold: false, kind: 'individual' },
      { name: 'Diamond Trading Co.', credit_limit: 30_000, on_hold: true, kind: 'corporate' },
    ],
  };
  const seededCustomers: any[] = [];
  for (const c of customerSets[industry]) {
    const cust = {
      id: uuid(),
      business_id: bizId,
      name: c.name,
      email: null,
      phone: null,
      address: null,
      kind: c.kind,
      credit_limit: c.credit_limit,
      payment_terms_days: 30,
      on_hold: c.on_hold,
      hold_reason: c.on_hold ? '90+ days overdue' : null,
      notes: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    _db.customers.push(cust);
    seededCustomers.push(cust);
  }

  // Aged unpaid invoices spread across buckets so AR Aging has data.
  const agingSchedule: Array<[number, number, 'sent' | 'partial' | 'overdue']> = [
    [12, 3_500, 'sent'], // 0-30
    [22, 8_750, 'sent'], // 0-30
    [40, 4_200, 'overdue'], // 31-60
    [55, 12_000, 'partial'], // 31-60
    [78, 6_500, 'overdue'], // 61-90
    [110, 9_400, 'overdue'], // 91+
  ];
  agingSchedule.forEach(([daysAgo, total, status], i) => {
    const customer = seededCustomers[i % seededCustomers.length];
    if (!customer) return;
    _db.invoices.push({
      id: uuid(),
      business_id: bizId,
      invoice_number: `INV-${(industry[0] ?? 'X').toUpperCase()}${1000 + i}`,
      customer_name: customer.name,
      customer_id: customer.id,
      issue_date: minus(daysAgo),
      due_date: minus(Math.max(0, daysAgo - 30)),
      subtotal: total,
      vat_amount: 0,
      total,
      status,
      paid_amount: status === 'partial' ? Math.round(total * 0.4) : 0,
      notes: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    });
  });

  const supplierSets = {
    rifle: [
      { name: 'PAAM (Pretoria Arms & Ammo)', balance: 0, on_hold: false },
      { name: 'Explosion Ammo Co.', balance: 18_500, on_hold: false },
      { name: 'Imports — Clever Mirage SA', balance: 32_000, on_hold: false },
    ],
    dental: [
      { name: 'Henry Schein South Africa', balance: 24_500, on_hold: false },
      { name: 'Dental Wholesale BW', balance: 5_800, on_hold: false },
      { name: 'Pretoria Dental Lab', balance: 9_200, on_hold: false },
    ],
    restaurant: [
      { name: 'Choppies Wholesale', balance: 12_400, on_hold: false },
      { name: 'Cape Wines (Pty) Ltd', balance: 18_900, on_hold: false },
      { name: 'Botswana Meat Commission', balance: 8_700, on_hold: false },
      { name: 'Fresh Produce Market', balance: 2_100, on_hold: false },
    ],
  };
  for (const s of supplierSets[industry]) {
    _db.suppliers.push({
      id: uuid(),
      business_id: bizId,
      name: s.name,
      contact_name: null,
      email: null,
      phone: null,
      address: null,
      payment_terms_days: 30,
      outstanding_balance: s.balance,
      on_hold: s.on_hold,
      notes: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    });
  }

  const employeeSets = {
    rifle: [
      {
        name: 'Mr Mokgwabone (Range Manager)',
        national_id: '123456789',
        position: 'Range Manager',
        basic: 12_000,
        transport: 1_500,
        housing: 0,
        pension_emp: 600,
        medical: 400,
      },
      {
        name: 'Ms Tebogo (Admin)',
        national_id: '123456790',
        position: 'Administrator',
        basic: 8_500,
        transport: 1_000,
        housing: 0,
        pension_emp: 425,
        medical: 300,
      },
      {
        name: 'Mr Lekoko (Range Marshal)',
        national_id: '123456791',
        position: 'Range Marshal',
        basic: 6_500,
        transport: 800,
        housing: 0,
        pension_emp: 325,
        medical: 200,
      },
    ],
    dental: [
      {
        name: 'Dr Pako Sebego (Principal)',
        national_id: '223456789',
        position: 'Principal Dentist',
        basic: 45_000,
        transport: 3_000,
        housing: 8_000,
        pension_emp: 2_250,
        medical: 1_500,
      },
      {
        name: 'Dr Naledi Botha (Associate)',
        national_id: '223456790',
        position: 'Associate Dentist',
        basic: 28_000,
        transport: 2_500,
        housing: 4_000,
        pension_emp: 1_400,
        medical: 1_000,
      },
      {
        name: 'Ms Boipelo (Hygienist)',
        national_id: '223456791',
        position: 'Dental Hygienist',
        basic: 14_000,
        transport: 1_500,
        housing: 0,
        pension_emp: 700,
        medical: 600,
      },
      {
        name: 'Ms Onthatile (Receptionist)',
        national_id: '223456792',
        position: 'Receptionist',
        basic: 8_500,
        transport: 1_000,
        housing: 0,
        pension_emp: 425,
        medical: 400,
      },
    ],
    restaurant: [
      {
        name: 'Chef Carlo Romano',
        national_id: '323456789',
        position: 'Head Chef',
        basic: 22_000,
        transport: 2_000,
        housing: 5_000,
        pension_emp: 1_100,
        medical: 800,
      },
      {
        name: 'Mr Khumo (Sous Chef)',
        national_id: '323456790',
        position: 'Sous Chef',
        basic: 12_000,
        transport: 1_500,
        housing: 0,
        pension_emp: 600,
        medical: 500,
      },
      {
        name: 'Ms Lesego (FoH Manager)',
        national_id: '323456791',
        position: 'FoH Manager',
        basic: 11_000,
        transport: 1_500,
        housing: 0,
        pension_emp: 550,
        medical: 500,
      },
      {
        name: 'Mr Tshepo (Server)',
        national_id: '323456792',
        position: 'Server',
        basic: 4_500,
        transport: 800,
        housing: 0,
        pension_emp: 225,
        medical: 0,
      },
      {
        name: 'Ms Mpho (Kitchen Hand)',
        national_id: '323456793',
        position: 'Kitchen Hand',
        basic: 4_200,
        transport: 800,
        housing: 0,
        pension_emp: 210,
        medical: 0,
      },
    ],
  };
  for (const e of employeeSets[industry]) {
    _db.employees.push({
      id: uuid(),
      business_id: bizId,
      name: e.name,
      national_id: e.national_id,
      position: e.position,
      start_date: '2024-01-01',
      end_date: null,
      basic_salary: e.basic,
      allowances: { transport: e.transport, housing: e.housing, other: 0 },
      deductions: { pension_employee: e.pension_emp, medical_aid: e.medical, loan: 0, other: 0 },
      benefits: {},
      is_active: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    });
  }
}

function seedBaseCoa(bizId: string) {
  const STD: Array<[string, string, 'asset' | 'liability' | 'equity' | 'income' | 'expense']> = [
    ['1000', 'Cash at Bank', 'asset'],
    ['1100', 'Trade Debtors Control', 'asset'],
    ['1200', 'Inventory', 'asset'],
    ['1300', 'Prepayments', 'asset'],
    ['1500', 'Fixed Assets at Cost', 'asset'],
    ['1510', 'Accumulated Depreciation', 'asset'],
    ['2110', 'VAT Input Receivable', 'asset'],
    ['2000', 'Trade Creditors Control', 'liability'],
    ['2100', 'VAT Output Payable', 'liability'],
    ['2200', 'PAYE Payable (BURS)', 'liability'],
    ['2210', 'Payroll Deductions Payable', 'liability'],
    ['2300', 'Loans Payable', 'liability'],
    ['3000', "Owner's Capital", 'equity'],
    ['3100', 'Retained Earnings', 'equity'],
    ['3200', 'Drawings', 'equity'],
    ['4200', 'Gain/(Loss) on Asset Disposal', 'income'],
    // Standard accounts the production seed (seed_botswana_coa) also carries.
    // The industry seeders don't use 5100/5900, so without these the payroll
    // run (5100) and depreciation run (5900) can't find their GL accounts and
    // silently no-op in offline/demo mode.
    ['5100', 'Salaries & Wages', 'expense'],
    ['5900', 'Depreciation', 'expense'],
  ];
  for (const [code, name, type] of STD) ensureAccount(bizId, code, name, type);
}

function seedRifleClub(): void {
  // Source: Gaborone Rifle Club Annual Financial Report, FY ended 31 Dec 2025.
  // Prepared by Prathap Ganesharajah, FCCA/FCA/ACMA. Net deficit (25,160.79),
  // ending cash BWP 249,944.78, debt-free. Capital purchase: Matarelli
  // Automatic Ball Trap @ BWP 108,536.23 (capitalised on the BS, not P&L).
  const userId = uuid();
  _db.users.push({ id: userId, email: 'rifleclub@nakopula.demo', password: 'admin' });
  _db.profiles.push({
    id: userId,
    email: 'rifleclub@nakopula.demo',
    phone: null,
    display_name: 'Gaborone Rifle Club',
    preferred_language: 'en',
    timezone: 'Africa/Gaborone',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  });

  const bizId = uuid();
  _db.businesses.push({
    id: bizId,
    owner_id: userId,
    name: 'Gaborone Rifle Club',
    trading_name: 'GRC',
    industry: 'Sports / Members club',
    vat_registered: false, // members club, not registered
    vat_number: null,
    fiscal_year_start_month: 1,
    base_currency: 'BWP',
    tier: 'pro',
    tier_renews_at: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  });

  seedBaseCoa(bizId);
  seedCommons({ bizId, userId, industry: 'rifle' });

  const cash = ensureAccount(bizId, '1000', 'Cash at Bank', 'asset');
  const fixedAssets = ensureAccount(bizId, '1500', 'Fixed Assets at Cost', 'asset');
  const ownerCapital = ensureAccount(bizId, '3000', "Owner's Capital", 'equity');

  // Income (4xxx) — per FY25 statement
  const acAmmoSales = ensureAccount(bizId, '4000', 'Ammo Sales', 'income');
  const acAmmoRecoveries = ensureAccount(
    bizId,
    '4010',
    'Ammo Purchase Recoveries / Adjustments',
    'income',
  );
  const acMembership = ensureAccount(bizId, '4020', 'Membership Fees', 'income');
  const acCorporate = ensureAccount(bizId, '4030', 'Corporate Membership Fees', 'income');

  // Direct expenses (5xxx)
  const acAmmoClay = ensureAccount(bizId, '5000', 'Ammo and Clay Purchases', 'expense');
  const acClayOp = ensureAccount(bizId, '5010', 'Clay Purchases (Operational)', 'expense');

  // Operating expenses (6xxx)
  const acCompExp = ensureAccount(bizId, '6000', 'Competition Expenses', 'expense');
  const acCompPrizes = ensureAccount(bizId, '6010', 'Competition Prizes', 'expense');
  const acBankCharges = ensureAccount(bizId, '6020', 'Bank Charges', 'expense');
  const acRepairs = ensureAccount(bizId, '6030', 'Repairs and Maintenance', 'expense');
  const acClayRepairs = ensureAccount(bizId, '6040', 'Clay Machine Repairs', 'expense');
  const acAdvertising = ensureAccount(bizId, '6050', 'Advertising / Billboards', 'expense');
  const acCleaning = ensureAccount(bizId, '6060', 'Cleaning', 'expense');
  const acFuel = ensureAccount(bizId, '6070', 'Fuel', 'expense');
  const acWater = ensureAccount(bizId, '6080', 'Water', 'expense');
  const acGiftsWages = ensureAccount(bizId, '6090', 'Gifts, Donations and Wages', 'expense');
  const acMicFix = ensureAccount(bizId, '6100', 'Mic Fixing', 'expense');
  const acDong = ensureAccount(bizId, '6110', 'Dong for Rifle Competition', 'expense');

  // Opening balance — 1 Jan 2024 (so FY24 + FY25 both flow as journal entries)
  // Pre-FY24 cash = FY25-opening cash 275,105.57 - FY24 surplus 21,165 = 253,940.57.
  // Fixed assets at start of FY24: existing machines (Promatic DTL, Promatic
  // Wobble, Matarelli ATA, manual machines, Single Stack) = BWP 266,687.50.
  const openingCash = 253_940.57;
  const openingFA = 266_687.5;
  const openingEquity = openingCash + openingFA;

  const eid = uuid();
  _db.journal_entries.push({
    id: eid,
    business_id: bizId,
    entry_date: '2024-01-01',
    reference: 'Opening balance FY24',
    description: 'Carried forward from FY23',
    source: 'manual',
    source_id: null,
    is_posted: true,
    is_locked: true,
    posted_at: new Date().toISOString(),
    created_by: userId,
    created_at: new Date().toISOString(),
  });
  _db.journal_lines.push(
    {
      id: uuid(),
      entry_id: eid,
      account_id: cash,
      debit: openingCash,
      credit: 0,
      vat_amount: 0,
      vat_rate: 0,
      description: 'Opening cash',
    },
    {
      id: uuid(),
      entry_id: eid,
      account_id: fixedAssets,
      debit: openingFA,
      credit: 0,
      vat_amount: 0,
      vat_rate: 0,
      description: 'Opening fixed assets',
    },
    {
      id: uuid(),
      entry_id: eid,
      account_id: ownerCapital,
      debit: 0,
      credit: openingEquity,
      vat_amount: 0,
      vat_rate: 0,
      description: 'Opening accumulated surplus',
    },
  );

  // ============================================================
  // FY24 Comparatives — per FY25 Annual Report comparative column
  // ============================================================
  postSimple(bizId, userId, '2024-12-31', 'FY24 Ammo Sales', cash, acAmmoSales, 621_450.0);
  postSimple(bizId, userId, '2024-12-31', 'FY24 Membership Fees', cash, acMembership, 64_800.0);
  postSimple(
    bizId,
    userId,
    '2024-12-31',
    'FY24 Corporate Membership Fees',
    cash,
    acCorporate,
    71_526.0,
  );

  postSimple(
    bizId,
    userId,
    '2024-12-31',
    'FY24 Ammo and Clay Purchases',
    acAmmoClay,
    cash,
    429_618.0,
  );
  postSimple(
    bizId,
    userId,
    '2024-12-31',
    'FY24 Clay Purchases (Operational)',
    acClayOp,
    cash,
    67_365.0,
  );
  postSimple(bizId, userId, '2024-12-31', 'FY24 Competition Expenses', acCompExp, cash, 52_140.0);
  postSimple(bizId, userId, '2024-12-31', 'FY24 Competition Prizes', acCompPrizes, cash, 14_500.0);
  postSimple(bizId, userId, '2024-12-31', 'FY24 Bank Charges', acBankCharges, cash, 6_540.0);
  postSimple(bizId, userId, '2024-12-31', 'FY24 Repairs and Maintenance', acRepairs, cash, 3_980.0);
  postSimple(
    bizId,
    userId,
    '2024-12-31',
    'FY24 Clay Machine Repairs',
    acClayRepairs,
    cash,
    2_100.0,
  );
  postSimple(
    bizId,
    userId,
    '2024-12-31',
    'FY24 Advertising / Billboards',
    acAdvertising,
    cash,
    1_800.0,
  );
  postSimple(bizId, userId, '2024-12-31', 'FY24 Cleaning', acCleaning, cash, 1_200.0);
  postSimple(bizId, userId, '2024-12-31', 'FY24 Fuel', acFuel, cash, 950.0);
  postSimple(bizId, userId, '2024-12-31', 'FY24 Water', acWater, cash, 1_524.0);
  postSimple(
    bizId,
    userId,
    '2024-12-31',
    'FY24 Gifts, Donations and Wages',
    acGiftsWages,
    cash,
    9_600.0,
  );
  // FY24 alignment per the FY25 annual report comparative column
  postSimple(
    bizId,
    userId,
    '2024-12-31',
    'FY24 Other Expenses (comparative alignment)',
    acGiftsWages,
    cash,
    6_483.0,
  );

  // FY25 Income
  postSimple(bizId, userId, '2025-12-31', 'FY25 Ammo Sales', cash, acAmmoSales, 686_777.5);
  postSimple(
    bizId,
    userId,
    '2025-12-31',
    'FY25 Ammo Purchase Recoveries / Adjustments',
    cash,
    acAmmoRecoveries,
    8_500.0,
  );
  postSimple(bizId, userId, '2025-12-31', 'FY25 Membership Fees', cash, acMembership, 71_425.0);
  postSimple(
    bizId,
    userId,
    '2025-12-31',
    'FY25 Corporate Membership Fees',
    cash,
    acCorporate,
    30_500.0,
  );

  // FY25 Direct expenses
  postSimple(
    bizId,
    userId,
    '2025-12-31',
    'FY25 Ammo and Clay Purchases',
    acAmmoClay,
    cash,
    513_860.63,
  );
  postSimple(
    bizId,
    userId,
    '2025-12-31',
    'FY25 Clay Purchases (Operational)',
    acClayOp,
    cash,
    71_504.05,
  );

  // FY25 Operating expenses
  postSimple(bizId, userId, '2025-12-31', 'FY25 Competition Expenses', acCompExp, cash, 60_368.94);
  postSimple(bizId, userId, '2025-12-31', 'FY25 Competition Prizes', acCompPrizes, cash, 17_300.0);
  postSimple(bizId, userId, '2025-12-31', 'FY25 Bank Charges', acBankCharges, cash, 8_503.92);
  postSimple(bizId, userId, '2025-12-31', 'FY25 Repairs and Maintenance', acRepairs, cash, 5_339.0);
  postSimple(
    bizId,
    userId,
    '2025-12-31',
    'FY25 Clay Machine Repairs',
    acClayRepairs,
    cash,
    2_708.64,
  );
  postSimple(
    bizId,
    userId,
    '2025-12-31',
    'FY25 Advertising / Billboards',
    acAdvertising,
    cash,
    3_000.0,
  );
  postSimple(bizId, userId, '2025-12-31', 'FY25 Cleaning', acCleaning, cash, 1_650.0);
  postSimple(bizId, userId, '2025-12-31', 'FY25 Fuel', acFuel, cash, 700.93);
  postSimple(bizId, userId, '2025-12-31', 'FY25 Water', acWater, cash, 2_181.0);
  postSimple(
    bizId,
    userId,
    '2025-12-31',
    'FY25 Gifts, Donations and Wages',
    acGiftsWages,
    cash,
    12_300.0,
  );
  postSimple(bizId, userId, '2025-12-31', 'FY25 Mic Fixing', acMicFix, cash, 7_966.67);
  postSimple(
    bizId,
    userId,
    '2025-12-31',
    'FY25 Dong for Rifle Competition',
    acDong,
    cash,
    6_443.28,
  );

  // Capital purchase — Matarelli Automatic Ball Trap (capitalised, not expensed)
  postSimple(
    bizId,
    userId,
    '2025-08-15',
    'Matarelli Automatic Ball Trap Clay Machine',
    fixedAssets,
    cash,
    108_536.23,
  );

  // Inventory at PAAM (per Note 3) — cost / sale prices indicative for demo.
  // GRC business rule: every ammo case sold is bundled with 25 clays
  // (shooters fire at flying clay targets), so each sale also draws down
  // clay stock. Clays are operating-expensed at purchase, so the clay
  // deduction is stock-only — no journal posting.
  const inv: Array<[string, string, number, number, number]> = [
    // sku, name, qty, cost_per_case, sale_per_case
    ['CM-T2-7.5-28gr', 'Clever Mirage T2 7.5 - 28gr', 3, 1_350, 1_700],
    ['CM-T2-7.5-24gr', 'Clever Mirage T2 7.5 - 24gr', 8, 1_280, 1_600],
    ['CM-T4-7.5-28gr', 'Clever Mirage T4 7.5 - 28gr', 26, 1_400, 1_750],
    ['CM-T2-7.5-21gr', 'Clever Mirage T2 7.5 - 21gr', 2, 1_220, 1_550],
    ['ELEY-7.5-28gr', 'Eley 7.5 - 28gr', 3, 1_650, 2_050],
  ];
  for (const [sku, name, qty, costPrice, salePrice] of inv) {
    _db.inventory_items.push({
      id: uuid(),
      business_id: bizId,
      sku,
      name,
      unit: 'case',
      cost_price: costPrice,
      sale_price: salePrice,
      qty_on_hand: qty,
      reorder_point: 5,
      is_active: true,
      // Bundled-consumable: each ammo case shoots 25 clays.
      bundled_sku: 'CLAYS',
      bundled_qty: 25,
      kind: 'ammo',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    });
  }

  // Clays — bundled-consumable companion stock for the ammo items.
  // Expensed at purchase under Clay Purchases (Operational), but tracked
  // separately so the system can deduct on every ammo sale.
  _db.inventory_items.push({
    id: uuid(),
    business_id: bizId,
    sku: 'CLAYS',
    name: 'Clay targets (bundled with ammo)',
    unit: 'clay',
    cost_price: null, // expensed at purchase, not held at cost
    sale_price: null,
    qty_on_hand: 8_000,
    reorder_point: 1_000,
    is_active: true,
    bundled_sku: null,
    bundled_qty: null,
    kind: 'consumable',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  });

  // Fixed Asset Register (per PDF Note 4 — Property, Plant and Equipment)
  const assets: Array<{
    name: string;
    category: string;
    cost: number;
    currency: 'BWP' | 'ZAR';
    acquired: string;
    location: string;
  }> = [
    {
      name: 'Promatic DTL Clay Machine',
      category: 'Clay machinery',
      cost: 59_000,
      currency: 'ZAR',
      acquired: '2019-08-01',
      location: 'Range A',
    },
    {
      name: 'Promatic Signature Wobble Doubles Clay Machine',
      category: 'Clay machinery',
      cost: 131_687.5,
      currency: 'ZAR',
      acquired: '2021-06-15',
      location: 'Range A',
    },
    {
      name: 'Matarelli ATA Clay Machine',
      category: 'Clay machinery',
      cost: 60_000,
      currency: 'BWP',
      acquired: '2022-03-10',
      location: 'Range B',
    },
    {
      name: 'Matarelli Automatic Ball Trap Clay Machine',
      category: 'Clay machinery',
      cost: 108_536.23,
      currency: 'BWP',
      acquired: '2025-08-15',
      location: 'Range B',
    },
    {
      name: 'Various sporting manual clay machines',
      category: 'Clay machinery',
      cost: 5_000,
      currency: 'BWP',
      acquired: '2020-01-01',
      location: 'Range A',
    },
    {
      name: 'Single Stack Clay Machine',
      category: 'Clay machinery',
      cost: 11_000,
      currency: 'BWP',
      acquired: '2023-09-12',
      location: 'Range C',
    },
  ];
  for (const a of assets) {
    _db.fixed_assets.push({
      id: uuid(),
      business_id: bizId,
      name: a.name,
      category: a.category,
      cost: a.cost,
      currency: a.currency,
      acquired_date: a.acquired,
      location: a.location,
      status: 'in_service',
      depreciation_method: 'straight_line',
      useful_life_years: 10,
      accumulated_depreciation: 0,
      notes: 'Per FY25 audited financial statements, Note 4',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    });
  }

  // Reminders (FY26-relevant)
  _db.reminders.push(
    {
      id: uuid(),
      business_id: bizId,
      type: 'custom',
      title: 'AGM 2026 — circulate FY25 financials',
      amount: null,
      due_date: '2026-03-15',
      status: 'pending',
      notify_via: ['app'],
      notified_at: null,
      completed_at: null,
      created_at: new Date().toISOString(),
    },
    {
      id: uuid(),
      business_id: bizId,
      type: 'supplier',
      title: 'PAAM ammo restock order',
      amount: 75_000,
      due_date: '2026-02-28',
      status: 'pending',
      notify_via: ['app'],
      notified_at: null,
      completed_at: null,
      created_at: new Date().toISOString(),
    },
    {
      id: uuid(),
      business_id: bizId,
      type: 'custom',
      title: 'Matarelli Auto Ball Trap — service',
      amount: null,
      due_date: '2026-06-30',
      status: 'pending',
      notify_via: ['app'],
      notified_at: null,
      completed_at: null,
      created_at: new Date().toISOString(),
    },
    {
      id: uuid(),
      business_id: bizId,
      type: 'custom',
      title: 'Insurance renewal — clay machinery',
      amount: null,
      due_date: '2026-04-01',
      status: 'pending',
      notify_via: ['app'],
      notified_at: null,
      completed_at: null,
      created_at: new Date().toISOString(),
    },
  );
}

function seedDental(): void {
  const userId = uuid();
  _db.users.push({ id: userId, email: 'paxdental@nakopula.demo', password: 'admin' });
  _db.profiles.push({
    id: userId,
    email: 'paxdental@nakopula.demo',
    phone: null,
    display_name: 'Pako Dental Studio',
    preferred_language: 'en',
    timezone: 'Africa/Gaborone',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  });
  const bizId = uuid();
  _db.businesses.push({
    id: bizId,
    owner_id: userId,
    name: 'Pako Dental Studio',
    trading_name: 'Pako Dental',
    industry: 'Healthcare / Dental',
    vat_registered: true,
    vat_number: 'P0001234567',
    fiscal_year_start_month: 7,
    base_currency: 'BWP',
    tier: 'smart',
    tier_renews_at: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  });
  seedBaseCoa(bizId);
  seedCommons({ bizId, userId, industry: 'dental' });

  const cash = ensureAccount(bizId, '1000', 'Cash at Bank', 'asset');
  ensureAccount(bizId, '4000', 'Patient Consultations', 'income');
  const acClean = ensureAccount(bizId, '4010', 'Cleanings & Polishing', 'income');
  const acFill = ensureAccount(bizId, '4020', 'Fillings & Restorations', 'income');
  const acRoot = ensureAccount(bizId, '4030', 'Root Canals', 'income');
  ensureAccount(bizId, '4040', 'Extractions', 'income');
  ensureAccount(bizId, '4050', 'Orthodontics / Braces', 'income');
  ensureAccount(bizId, '4060', 'Whitening', 'income');
  ensureAccount(bizId, '5000', 'Dental Supplies', 'expense');
  const acLab = ensureAccount(bizId, '5010', 'Lab Bills (crowns, bridges)', 'expense');
  ensureAccount(bizId, '5020', 'Sterilisation Supplies', 'expense');
  ensureAccount(bizId, '5030', 'X-Ray Film & Imaging', 'expense');
  const acPpe = ensureAccount(bizId, '5040', 'Disposable PPE', 'expense');
  ensureAccount(bizId, '5050', 'Equipment Maintenance', 'expense');
  ensureAccount(bizId, '5060', 'Continuing Education', 'expense');

  const today = new Date();
  const isoMinus = (days: number) => {
    const d = new Date(today);
    d.setDate(d.getDate() - days);
    return d.toISOString().slice(0, 10);
  };

  postSimple(bizId, userId, isoMinus(14), 'Cleaning - Mr Khumalo', cash, acClean, 850);
  postSimple(bizId, userId, isoMinus(10), 'Filling - Ms Modise', cash, acFill, 1_500);
  postSimple(bizId, userId, isoMinus(7), 'Root canal - Mr Pheto', cash, acRoot, 4_500);
  postSimple(bizId, userId, isoMinus(5), 'Crown lab bill', acLab, cash, 1_200);
  postSimple(bizId, userId, isoMinus(3), 'PPE supplies', acPpe, cash, 480);

  const future = (days: number) => {
    const d = new Date(today);
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
  };

  // Dental inventory — consumables and supplies
  const dentalInv: Array<[string, string, number, string, number]> = [
    ['DS-LIDOCAINE-2', 'Lidocaine 2% cartridges', 120, 'cartridge', 25],
    ['DS-COMPOSITE-A2', 'Composite resin shade A2', 18, 'syringe', 5],
    ['DS-GLOVES-M', 'Nitrile gloves (size M)', 4_800, 'pair', 500],
    ['DS-MASKS-N95', 'Surgical masks N95', 920, 'piece', 200],
    ['DS-XRAY-FILM', 'X-ray film D-speed', 260, 'pack', 30],
    ['DS-AUTOCLAVE-POUCH', 'Autoclave sterilisation pouches', 1_500, 'piece', 200],
    ['DS-ENDODONTIC-FILE', 'Endodontic K-files (size 25)', 96, 'piece', 20],
  ];
  for (const [sku, name, qty, unit, reorder] of dentalInv) {
    _db.inventory_items.push({
      id: uuid(),
      business_id: bizId,
      sku,
      name,
      unit,
      cost_price: null,
      sale_price: null,
      qty_on_hand: qty,
      reorder_point: reorder,
      is_active: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    });
  }

  // Dental fixed assets
  const dentalAssets: Array<[string, string, number, string, string]> = [
    [
      'Sirona Dental Chair (Treatment Room 1)',
      'Dental equipment',
      285_000,
      '2023-04-01',
      'Treatment Room 1',
    ],
    [
      'Sirona Dental Chair (Treatment Room 2)',
      'Dental equipment',
      285_000,
      '2024-08-15',
      'Treatment Room 2',
    ],
    ['Planmeca Intraoral X-ray Unit', 'Imaging', 145_000, '2023-04-01', 'Treatment Room 1'],
    ['Carestream Panoramic X-ray', 'Imaging', 320_000, '2024-02-10', 'Imaging Suite'],
    ['Tuttnauer Autoclave Steriliser', 'Sterilisation', 78_500, '2023-04-01', 'Sterilisation Room'],
    ['NSK Surgic Pro Implant Motor', 'Surgical', 92_000, '2025-01-20', 'Treatment Room 2'],
    ['Cavitron Ultrasonic Scaler', 'Hygiene', 24_500, '2023-04-01', 'Treatment Room 1'],
  ];
  for (const [name, category, cost, acquired, location] of dentalAssets) {
    _db.fixed_assets.push({
      id: uuid(),
      business_id: bizId,
      name,
      category,
      cost,
      currency: 'BWP',
      acquired_date: acquired,
      location,
      status: 'in_service',
      depreciation_method: 'straight_line',
      useful_life_years: 7,
      accumulated_depreciation: 0,
      notes: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    });
  }

  _db.reminders.push(
    {
      id: uuid(),
      business_id: bizId,
      type: 'vat',
      title: 'VAT 002 quarterly return',
      amount: null,
      due_date: future(18),
      status: 'pending',
      notify_via: ['app'],
      notified_at: null,
      completed_at: null,
      created_at: new Date().toISOString(),
    },
    {
      id: uuid(),
      business_id: bizId,
      type: 'custom',
      title: 'Autoclave service contract',
      amount: null,
      due_date: future(30),
      status: 'pending',
      notify_via: ['app'],
      notified_at: null,
      completed_at: null,
      created_at: new Date().toISOString(),
    },
    {
      id: uuid(),
      business_id: bizId,
      type: 'paye',
      title: 'PAYE for hygienist & assistant',
      amount: null,
      due_date: future(7),
      status: 'pending',
      notify_via: ['app'],
      notified_at: null,
      completed_at: null,
      created_at: new Date().toISOString(),
    },
  );

  // ─── Dental patient roster ────────────────────────────────────────
  // Four patients across the typical mix: routine cleaning regular, a
  // restorative case, a child orthodontic plan, and one with active
  // antibiotic prescription. follow_up_at on the first three is set
  // within 14 days so the Upcoming Recalls widget has live data.
  const isoMinusTime = (days: number) => {
    const d = new Date(today);
    d.setDate(d.getDate() - days);
    return d.toISOString();
  };
  const futureDate = (days: number) => {
    const d = new Date(today);
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
  };

  const patientSeeds: Array<{
    first: string;
    last: string;
    dob: string;
    gender: 'male' | 'female';
    omang: string;
    phone: string;
    medicalAid: string | null;
    chronic: string | null;
    allergies: string | null;
    bloodGroup: string | null;
    lastVisitDaysAgo: number;
  }> = [
    {
      first: 'Thabo',
      last: 'Khumalo',
      dob: '1985-03-12',
      gender: 'male',
      omang: '385031200',
      phone: '+267 71 234 567',
      medicalAid: 'BPOMAS',
      chronic: null,
      allergies: 'Penicillin',
      bloodGroup: 'O+',
      lastVisitDaysAgo: 14,
    },
    {
      first: 'Naledi',
      last: 'Modise',
      dob: '1992-08-22',
      gender: 'female',
      omang: '392082290',
      phone: '+267 72 345 678',
      medicalAid: 'BOMaid',
      chronic: 'Hypertension',
      allergies: null,
      bloodGroup: 'A+',
      lastVisitDaysAgo: 10,
    },
    {
      first: 'Kgosi',
      last: 'Pheto',
      dob: '2012-11-04',
      gender: 'male',
      omang: '512110498',
      phone: '+267 73 456 789',
      medicalAid: 'Pula Medical Aid',
      chronic: null,
      allergies: null,
      bloodGroup: 'B+',
      lastVisitDaysAgo: 7,
    },
    {
      first: 'Lerato',
      last: 'Sebina',
      dob: '1978-05-30',
      gender: 'female',
      omang: '278053015',
      phone: '+267 74 567 890',
      medicalAid: null,
      chronic: 'Diabetes type 2',
      allergies: 'Latex',
      bloodGroup: 'AB-',
      lastVisitDaysAgo: 2,
    },
  ];

  const seededPatientIds: string[] = [];
  for (const p of patientSeeds) {
    const pid = uuid();
    seededPatientIds.push(pid);
    _db.patients.push({
      id: pid,
      business_id: bizId,
      first_name: p.first,
      last_name: p.last,
      date_of_birth: p.dob,
      gender: p.gender,
      national_id: p.omang,
      phone: p.phone,
      email: null,
      address: null,
      next_of_kin_name: null,
      next_of_kin_phone: null,
      next_of_kin_relationship: null,
      medical_aid_scheme: p.medicalAid,
      medical_aid_member_number: p.medicalAid ? `MA-${p.omang.slice(0, 6)}` : null,
      blood_group: p.bloodGroup,
      known_allergies: p.allergies,
      chronic_conditions: p.chronic,
      consent_to_store_records: true,
      consent_recorded_at: isoMinusTime(p.lastVisitDaysAgo + 1),
      registered_at: isoMinusTime(p.lastVisitDaysAgo + 30),
      last_visit_at: isoMinusTime(p.lastVisitDaysAgo),
      is_active: true,
      deleted_at: null,
      created_by: userId,
      created_at: isoMinusTime(p.lastVisitDaysAgo + 30),
      updated_at: isoMinusTime(p.lastVisitDaysAgo),
    });
  }

  // Visits — one per patient, mix of visit types. First three carry a
  // follow_up_at within the next two weeks so the recall widget lights up.
  const visitSeeds: Array<{
    patientIdx: number;
    daysAgo: number;
    type: 'consultation' | 'follow_up' | 'procedure' | 'prescription';
    complaint: string;
    diagnosis: string;
    treatment: string;
    amount: number;
    paymentStatus: 'unpaid' | 'paid' | 'medical_aid' | 'waived';
    followUpInDays: number | null;
    prescriptions?: Array<{
      drug: string;
      strength: string;
      form: string;
      dosage: string;
      frequency: string;
      duration: string;
      qty: number;
      instructions: string;
    }>;
  }> = [
    {
      patientIdx: 0,
      daysAgo: 14,
      type: 'procedure',
      complaint: 'Routine 6-month dental check-up and cleaning',
      diagnosis: 'Mild gingivitis on upper molars, mild calculus build-up',
      treatment: 'Scale and polish completed. Patient counselled on flossing technique.',
      amount: 850,
      paymentStatus: 'medical_aid',
      followUpInDays: 7, // recall soon — within 14d window
    },
    {
      patientIdx: 1,
      daysAgo: 10,
      type: 'procedure',
      complaint: 'Pain on biting, upper left quadrant',
      diagnosis: 'Deep occlusal caries on tooth #26',
      treatment: 'Composite filling placed on #26 (MOD). Local anaesthesia: lidocaine 2%.',
      amount: 1_500,
      paymentStatus: 'medical_aid',
      followUpInDays: 12, // recall within 14d
      prescriptions: [
        {
          drug: 'Ibuprofen',
          strength: '400mg',
          form: 'tablet',
          dosage: '1 tab',
          frequency: 'TDS',
          duration: '3 days',
          qty: 9,
          instructions: 'After meals, only if pain',
        },
      ],
    },
    {
      patientIdx: 2,
      daysAgo: 7,
      type: 'consultation',
      complaint: 'Crowding upper anterior teeth — parent referral',
      diagnosis: 'Class I malocclusion with anterior crowding (~4mm)',
      treatment: 'Orthodontic assessment, panoramic X-ray ordered. Treatment plan discussed.',
      amount: 650,
      paymentStatus: 'paid',
      followUpInDays: 10, // recall — orthodontic review
    },
    {
      patientIdx: 3,
      daysAgo: 2,
      type: 'procedure',
      complaint: 'Severe pain on tooth #36, swelling on left jaw 2 days',
      diagnosis: 'Acute apical abscess on #36 — irreversible pulpitis',
      treatment:
        'Emergency drainage. Root canal initiated (canals located + irrigated). Temporary dressing. Antibiotics prescribed. Diabetes status flagged — coordinated with GP.',
      amount: 2_800,
      paymentStatus: 'unpaid',
      followUpInDays: 5, // root canal completion appointment
      prescriptions: [
        {
          drug: 'Amoxicillin',
          strength: '500mg',
          form: 'capsule',
          dosage: '1 cap',
          frequency: 'TDS',
          duration: '7 days',
          qty: 21,
          instructions: 'Take with food. Complete the full course even if pain improves.',
        },
        {
          drug: 'Metronidazole',
          strength: '400mg',
          form: 'tablet',
          dosage: '1 tab',
          frequency: 'TDS',
          duration: '7 days',
          qty: 21,
          instructions: 'Take with food. Avoid alcohol entirely during course.',
        },
        {
          drug: 'Paracetamol',
          strength: '1g',
          form: 'tablet',
          dosage: '1 tab',
          frequency: 'QDS',
          duration: '5 days',
          qty: 20,
          instructions: 'For pain. Max 4g per day.',
        },
      ],
    },
  ];

  for (const v of visitSeeds) {
    const visitId = uuid();
    const patientId = seededPatientIds[v.patientIdx];
    if (!patientId) continue;
    _db.patient_visits.push({
      id: visitId,
      business_id: bizId,
      patient_id: patientId,
      visit_date: isoMinus(v.daysAgo),
      visit_type: v.type,
      chief_complaint: v.complaint,
      vitals: {},
      examination: null,
      diagnosis: v.diagnosis,
      icd10_codes: [],
      treatment_notes: v.treatment,
      follow_up_at: v.followUpInDays !== null ? futureDate(v.followUpInDays) : null,
      amount_charged: v.amount,
      payment_status: v.paymentStatus,
      invoice_id: null,
      attended_by: userId,
      is_locked: v.daysAgo >= 7, // older visits locked for audit integrity
      locked_at: v.daysAgo >= 7 ? isoMinusTime(v.daysAgo - 1) : null,
      attachments: [],
      deleted_at: null,
      created_at: isoMinusTime(v.daysAgo),
      updated_at: isoMinusTime(v.daysAgo),
    });

    for (const rx of v.prescriptions ?? []) {
      _db.patient_prescriptions.push({
        id: uuid(),
        business_id: bizId,
        visit_id: visitId,
        patient_id: patientId,
        drug_name: rx.drug,
        strength: rx.strength,
        form: rx.form,
        dosage: rx.dosage,
        frequency: rx.frequency,
        duration: rx.duration,
        quantity: rx.qty,
        instructions: rx.instructions,
        is_dispensed: false,
        dispensed_at: null,
        dispensed_by: null,
        created_at: isoMinusTime(v.daysAgo),
      });
    }

    // Audit trail — at minimum a create_visit row + view rows, matching
    // the real triggers in production.
    _db.patient_audit_log.push(
      {
        id: uuid(),
        business_id: bizId,
        patient_id: patientId,
        visit_id: visitId,
        actor_id: userId,
        action: 'create_visit',
        context: {},
        created_at: isoMinusTime(v.daysAgo),
      },
      {
        id: uuid(),
        business_id: bizId,
        patient_id: patientId,
        visit_id: visitId,
        actor_id: userId,
        action: 'view_visit',
        context: { source: 'demo_seed' },
        created_at: isoMinusTime(Math.max(0, v.daysAgo - 1)),
      },
    );
  }
}

function seedRestaurant(): void {
  const userId = uuid();
  _db.users.push({ id: userId, email: 'lunarosa@nakopula.demo', password: 'admin' });
  _db.profiles.push({
    id: userId,
    email: 'lunarosa@nakopula.demo',
    phone: null,
    display_name: 'Luna Rosa Restaurant',
    preferred_language: 'en',
    timezone: 'Africa/Gaborone',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  });
  const bizId = uuid();
  _db.businesses.push({
    id: bizId,
    owner_id: userId,
    name: 'Luna Rosa Restaurant',
    trading_name: 'Luna Rosa',
    industry: 'Hospitality / Restaurant',
    vat_registered: true,
    vat_number: 'P0009876543',
    fiscal_year_start_month: 7,
    base_currency: 'BWP',
    tier: 'smart',
    tier_renews_at: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  });
  seedBaseCoa(bizId);
  seedCommons({ bizId, userId, industry: 'restaurant' });

  const cash = ensureAccount(bizId, '1000', 'Cash at Bank', 'asset');
  const acDine = ensureAccount(bizId, '4000', 'Food Sales — Dine-in', 'income');
  ensureAccount(bizId, '4010', 'Food Sales — Takeaway', 'income');
  ensureAccount(bizId, '4020', 'Beverage Sales — Soft drinks', 'income');
  const acWine = ensureAccount(bizId, '4030', 'Beverage Sales — Wine', 'income');
  ensureAccount(bizId, '4040', 'Beverage Sales — Spirits', 'income');
  ensureAccount(bizId, '4050', 'Service Charge / Tips banked', 'income');
  ensureAccount(bizId, '4060', 'Function / Booking Revenue', 'income');
  const acMeat = ensureAccount(bizId, '5000', 'Food Cost — Meat & Poultry', 'expense');
  const acProduce = ensureAccount(bizId, '5010', 'Food Cost — Produce', 'expense');
  ensureAccount(bizId, '5020', 'Food Cost — Dry Goods', 'expense');
  ensureAccount(bizId, '5030', 'Beverage Cost', 'expense');
  const acKitchen = ensureAccount(bizId, '5040', 'Kitchen Wages', 'expense');
  ensureAccount(bizId, '5050', 'Front-of-house Wages', 'expense');
  const acGas = ensureAccount(bizId, '5060', 'Gas & Cooking Fuel', 'expense');
  ensureAccount(bizId, '5070', 'Cleaning Supplies', 'expense');
  ensureAccount(bizId, '5080', 'Linen & Laundry', 'expense');
  ensureAccount(bizId, '5090', 'Crockery & Glassware Replacement', 'expense');

  const today = new Date();
  const isoMinus = (days: number) => {
    const d = new Date(today);
    d.setDate(d.getDate() - days);
    return d.toISOString().slice(0, 10);
  };

  postSimple(bizId, userId, isoMinus(7), 'Saturday dinner takings', cash, acDine, 18_540);
  postSimple(bizId, userId, isoMinus(7), 'Wine sales Saturday', cash, acWine, 4_280);
  postSimple(bizId, userId, isoMinus(5), 'Choppies meat order', acMeat, cash, 6_800);
  postSimple(bizId, userId, isoMinus(5), 'Fresh produce - market', acProduce, cash, 2_100);
  postSimple(bizId, userId, isoMinus(3), 'Kitchen wages weekly', acKitchen, cash, 8_500);
  postSimple(bizId, userId, isoMinus(1), 'Gas refill', acGas, cash, 1_450);

  // Restaurant inventory — daily-use stock
  const restoInv: Array<[string, string, number, string, number]> = [
    ['BEEF-FILLET', 'Beef fillet (kg)', 12, 'kg', 5],
    ['CHICKEN-BREAST', 'Chicken breast (kg)', 28, 'kg', 10],
    ['SEABASS', 'Sea bass fillet (kg)', 6, 'kg', 3],
    ['POTATO', 'Potatoes (kg)', 45, 'kg', 20],
    ['ONIONS', 'Onions (kg)', 18, 'kg', 10],
    ['TOMATOES', 'Roma tomatoes (kg)', 14, 'kg', 8],
    ['OLIVE-OIL', 'Extra virgin olive oil (L)', 9, 'litre', 4],
    ['WINE-CAB-2022', 'Cabernet Sauvignon 2022', 42, 'bottle', 12],
    ['WINE-SAV-2023', 'Sauvignon Blanc 2023', 36, 'bottle', 12],
    ['BEER-LAGER', 'Lager (330ml)', 240, 'bottle', 96],
    ['SODA-COKE', 'Coke (300ml)', 180, 'bottle', 72],
    ['NAPKINS-LINEN', 'Linen napkins', 120, 'piece', 50],
  ];
  for (const [sku, name, qty, unit, reorder] of restoInv) {
    _db.inventory_items.push({
      id: uuid(),
      business_id: bizId,
      sku,
      name,
      unit,
      cost_price: null,
      sale_price: null,
      qty_on_hand: qty,
      reorder_point: reorder,
      is_active: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    });
  }

  // Restaurant fixed assets — kitchen + front-of-house equipment
  const restoAssets: Array<[string, string, number, string, string]> = [
    [
      'Rational SelfCookingCenter Combi Oven',
      'Kitchen equipment',
      195_000,
      '2022-09-01',
      'Main kitchen',
    ],
    ['Bonnet 6-burner gas range', 'Kitchen equipment', 68_000, '2022-09-01', 'Main kitchen'],
    [
      'Williams under-counter refrigeration (×3)',
      'Refrigeration',
      112_000,
      '2022-09-01',
      'Main kitchen',
    ],
    ['Walk-in cold room (1.8m × 2.4m)', 'Refrigeration', 145_000, '2022-09-01', 'Back of house'],
    ['Rocket Espresso Appartamento (×2)', 'Beverage equipment', 56_000, '2023-02-14', 'Bar'],
    ['Mahmayi 4-seater dining table set (×8)', 'Furniture', 96_000, '2022-09-01', 'Dining area'],
    ['Smeg dishwasher commercial', 'Kitchen equipment', 48_500, '2024-05-10', 'Wash area'],
  ];
  for (const [name, category, cost, acquired, location] of restoAssets) {
    _db.fixed_assets.push({
      id: uuid(),
      business_id: bizId,
      name,
      category,
      cost,
      currency: 'BWP',
      acquired_date: acquired,
      location,
      status: 'in_service',
      depreciation_method: 'straight_line',
      useful_life_years: 8,
      accumulated_depreciation: 0,
      notes: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    });
  }

  const future = (days: number) => {
    const d = new Date(today);
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
  };
  _db.reminders.push(
    {
      id: uuid(),
      business_id: bizId,
      type: 'vat',
      title: 'VAT 002 quarterly return',
      amount: null,
      due_date: future(18),
      status: 'pending',
      notify_via: ['app'],
      notified_at: null,
      completed_at: null,
      created_at: new Date().toISOString(),
    },
    {
      id: uuid(),
      business_id: bizId,
      type: 'paye',
      title: 'PAYE for kitchen + service team',
      amount: null,
      due_date: future(7),
      status: 'pending',
      notify_via: ['app'],
      notified_at: null,
      completed_at: null,
      created_at: new Date().toISOString(),
    },
    {
      id: uuid(),
      business_id: bizId,
      type: 'supplier',
      title: 'Wine restock - Cape Wines',
      amount: null,
      due_date: future(5),
      status: 'pending',
      notify_via: ['app'],
      notified_at: null,
      completed_at: null,
      created_at: new Date().toISOString(),
    },
    {
      id: uuid(),
      business_id: bizId,
      type: 'custom',
      title: 'Liquor licence renewal',
      amount: null,
      due_date: future(45),
      status: 'pending',
      notify_via: ['app'],
      notified_at: null,
      completed_at: null,
      created_at: new Date().toISOString(),
    },
  );
}

if (_db.users.length === 0) {
  seedRifleClub();
  seedDental();
  seedRestaurant();
  save(_db);
  console.warn(
    '[mock-supabase] Seeded demo data: rifleclub@/paxdental@/lunarosa@nakopula.demo, password admin',
  );
}

// ============================================================
// Query builder
// ============================================================

interface Filter {
  op: 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'in' | 'is';
  col: string;
  val: any;
}

class QueryBuilder<T = any> {
  private _filters: Filter[] = [];
  private _order: { col: string; ascending: boolean } | null = null;
  private _limit: number | null = null;
  private _single = false;
  private _maybeSingle = false;
  private _action: 'select' | 'insert' | 'update' | 'delete' | 'upsert' = 'select';
  private _payload: any = null;

  constructor(private table: Table) {}

  select(_cols?: string): this {
    this._action = this._action === 'insert' || this._action === 'update' ? this._action : 'select';
    return this;
  }
  insert(payload: any): this {
    this._action = 'insert';
    this._payload = payload;
    return this;
  }
  update(payload: any): this {
    this._action = 'update';
    this._payload = payload;
    return this;
  }
  upsert(payload: any): this {
    this._action = 'upsert';
    this._payload = payload;
    return this;
  }
  delete(): this {
    this._action = 'delete';
    return this;
  }

  eq(col: string, val: any): this {
    this._filters.push({ op: 'eq', col, val });
    return this;
  }
  neq(col: string, val: any): this {
    this._filters.push({ op: 'neq', col, val });
    return this;
  }
  gt(col: string, val: any): this {
    this._filters.push({ op: 'gt', col, val });
    return this;
  }
  gte(col: string, val: any): this {
    this._filters.push({ op: 'gte', col, val });
    return this;
  }
  lt(col: string, val: any): this {
    this._filters.push({ op: 'lt', col, val });
    return this;
  }
  lte(col: string, val: any): this {
    this._filters.push({ op: 'lte', col, val });
    return this;
  }
  in(col: string, vals: any[]): this {
    this._filters.push({ op: 'in', col, val: vals });
    return this;
  }
  is(col: string, val: any): this {
    this._filters.push({ op: 'is', col, val });
    return this;
  }

  order(col: string, opts?: { ascending?: boolean }): this {
    this._order = { col, ascending: opts?.ascending ?? true };
    return this;
  }

  limit(n: number): this {
    this._limit = n;
    return this;
  }
  single(): Promise<{ data: T | null; error: any }> {
    this._single = true;
    return this._exec();
  }
  maybeSingle(): Promise<{ data: T | null; error: any }> {
    this._maybeSingle = true;
    return this._exec();
  }

  then<TResult1 = { data: T[] | T | null; error: any }, TResult2 = never>(
    onFulfilled?:
      | ((value: { data: T[] | T | null; error: any }) => TResult1 | PromiseLike<TResult1>)
      | null,
    onRejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | null,
  ): Promise<TResult1 | TResult2> {
    return this._exec().then(onFulfilled as any, onRejected) as any;
  }

  private match(row: any): boolean {
    for (const f of this._filters) {
      const cell = row[f.col];
      switch (f.op) {
        case 'eq':
          if (cell !== f.val) return false;
          break;
        case 'neq':
          if (cell === f.val) return false;
          break;
        case 'gt':
          if (!(cell > f.val)) return false;
          break;
        case 'gte':
          if (!(cell >= f.val)) return false;
          break;
        case 'lt':
          if (!(cell < f.val)) return false;
          break;
        case 'lte':
          if (!(cell <= f.val)) return false;
          break;
        case 'in':
          if (!Array.isArray(f.val) || !f.val.includes(cell)) return false;
          break;
        case 'is':
          if (cell !== f.val) return false;
          break;
      }
    }
    return true;
  }

  private async _exec(): Promise<{ data: any; error: any }> {
    try {
      const list = (_db as any)[this.table] as any[];
      if (!Array.isArray(list))
        return { data: null, error: { message: `unknown table ${this.table}` } };

      if (this._action === 'select') {
        let rows = list.filter((r) => this.match(r));
        if (this._order) {
          const o = this._order;
          rows = [...rows].sort((a, b) => {
            const av = a[o.col];
            const bv = b[o.col];
            if (av === bv) return 0;
            if (av == null) return 1;
            if (bv == null) return -1;
            return o.ascending ? (av < bv ? -1 : 1) : av < bv ? 1 : -1;
          });
        }
        if (this._limit != null) rows = rows.slice(0, this._limit);
        if (this._single) {
          if (rows.length !== 1)
            return { data: null, error: { message: 'expected single row', code: 'PGRST116' } };
          return { data: rows[0], error: null };
        }
        if (this._maybeSingle) return { data: rows[0] ?? null, error: null };
        return { data: rows, error: null };
      }

      if (this._action === 'insert') {
        const items = Array.isArray(this._payload) ? this._payload : [this._payload];
        const inserted: any[] = [];
        for (const item of items) {
          const row = {
            id: item.id ?? uuid(),
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            ...item,
          };
          list.push(row);
          inserted.push(row);
        }
        save(_db);
        if (this._single) return { data: inserted[0], error: null };
        if (this._maybeSingle) return { data: inserted[0] ?? null, error: null };
        return { data: inserted, error: null };
      }

      if (this._action === 'update') {
        const updated: any[] = [];
        for (let i = 0; i < list.length; i++) {
          if (this.match(list[i])) {
            list[i] = { ...list[i], ...this._payload, updated_at: new Date().toISOString() };
            updated.push(list[i]);
          }
        }
        save(_db);
        if (this._single) return { data: updated[0] ?? null, error: null };
        if (this._maybeSingle) return { data: updated[0] ?? null, error: null };
        return { data: updated, error: null };
      }

      if (this._action === 'delete') {
        const before = list.length;
        const remaining = list.filter((r) => !this.match(r));
        (_db as any)[this.table] = remaining;
        save(_db);
        return { data: { deleted: before - remaining.length }, error: null };
      }

      return { data: null, error: { message: `unsupported action ${this._action}` } };
    } catch (e: any) {
      return { data: null, error: { message: e?.message ?? String(e) } };
    }
  }
}

// ============================================================
// Auth
// ============================================================

const auth = {
  async getSession(): Promise<{ data: { session: MockSession | null } }> {
    return { data: { session: _session } };
  },

  async signInWithPassword({ email, password }: { email: string; password: string }) {
    const u = _db.users.find((x) => x.email === email && x.password === password);
    if (!u) return { data: null, error: { message: 'Invalid login credentials' } };
    _session = {
      user: { id: u.id, email: u.email },
      access_token: 'mock',
      refresh_token: 'mock',
      expires_in: 3600,
      token_type: 'bearer',
    };
    localStorage.setItem(LS_SESSION, JSON.stringify(_session));
    notifyAuth('SIGNED_IN');
    return { data: { user: _session.user, session: _session }, error: null };
  },

  async signInWithOtp({ email }: { email: string; options?: any }) {
    // Mock: just accept any email and create the user, sign them in immediately.
    let u = _db.users.find((x) => x.email === email);
    if (!u) {
      const id = uuid();
      u = { id, email, password: 'magic-link' };
      _db.users.push(u);
      _db.profiles.push({
        id,
        email,
        phone: null,
        display_name: null,
        preferred_language: 'en',
        timezone: 'Africa/Gaborone',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
      save(_db);
    }
    _session = {
      user: { id: u.id, email: u.email },
      access_token: 'mock',
      refresh_token: 'mock',
      expires_in: 3600,
      token_type: 'bearer',
    };
    localStorage.setItem(LS_SESSION, JSON.stringify(_session));
    notifyAuth('SIGNED_IN');
    return { data: {}, error: null };
  },

  async signUp({
    email,
    password: _password,
  }: {
    email: string;
    password: string;
    options?: unknown;
  }) {
    // Create a new user in the mock — reuse the demo user ID for simplicity
    const id = 'signup-' + Date.now().toString(36);
    _session = {
      user: { id, email },
      access_token: 'mock' as const,
      refresh_token: 'mock' as const,
      expires_in: 3600,
      token_type: 'bearer',
    };
    localStorage.setItem(LS_SESSION, JSON.stringify(_session));
    notifyAuth('SIGNED_IN');
    return { data: { user: { id, email } }, error: null };
  },

  async signOut() {
    _session = null;
    localStorage.removeItem(LS_SESSION);
    notifyAuth('SIGNED_OUT');
    return { error: null };
  },

  onAuthStateChange(cb: (event: string, session: MockSession | null) => void) {
    authListeners.push(cb);
    return {
      data: {
        subscription: {
          unsubscribe: () => {
            const i = authListeners.indexOf(cb);
            if (i >= 0) authListeners.splice(i, 1);
          },
        },
      },
    };
  },
};

// ============================================================
// Storage (mock — returns blob: URLs, no actual upload)
// ============================================================

const storage = {
  from(_bucket: string) {
    return {
      async upload(_path: string, _file: Blob | File, _opts?: any) {
        return { data: { path: _path }, error: null };
      },
      getPublicUrl(path: string) {
        return { data: { publicUrl: `mock://storage/${path}` } };
      },
      async createSignedUrl(path: string, _expiresIn: number) {
        return { data: { signedUrl: `mock://storage/${path}` }, error: null };
      },
    };
  },
};

// ============================================================
// Edge functions (stub responses for demo flow)
// ============================================================

const merchantPool = [
  'Choppies',
  'Spar Botswana',
  'Engen',
  'BTC',
  'BPC',
  'Game Stores',
  'Builders Warehouse',
  'FNB',
  'Caltex',
  'Orange Botswana',
];

const functions = {
  async invoke(name: string, opts?: { body?: any }) {
    if (name === 'scan-receipt') {
      const { receipt_id, business_id } = opts?.body ?? {};
      const merchant = merchantPool[Math.floor(Math.random() * merchantPool.length)]!;
      const total = Math.round(Math.random() * 1500 + 50);
      const vat = Math.round((total * 0.14) / 1.14);
      const net = total - vat;

      const today = new Date().toISOString().slice(0, 10);
      const updated: any = {
        ocr_status: 'completed',
        ocr_tier_used: 2,
        ocr_confidence: 0.92,
        merchant_name: merchant,
        receipt_date: today,
        total_amount: total,
        vat_amount: vat,
        vat_rate: 14,
        line_items: [{ description: 'Items', quantity: 1, unit_price: total, line_total: total }],
        category_suggested: 'Other Expenses',
      };

      const r = _db.receipts.find((x) => x.id === receipt_id);
      if (r) Object.assign(r, updated);

      // Auto-post a journal entry (Dr Expense + VAT input / Cr Cash)
      const cash = _db.accounts.find((a) => a.business_id === business_id && a.gl_code === '1000');
      const expense =
        _db.accounts.find((a) => a.business_id === business_id && a.gl_code === '6000') ??
        _db.accounts.find((a) => a.business_id === business_id && a.gl_code === '5600');
      const vatInput = _db.accounts.find(
        (a) => a.business_id === business_id && a.gl_code === '2110',
      );

      if (cash && expense && vatInput && r) {
        const eid = uuid();
        _db.journal_entries.push({
          id: eid,
          business_id,
          entry_date: today,
          reference: merchant,
          description: `Receipt - ${merchant}`,
          source: 'receipt',
          source_id: receipt_id,
          is_posted: true,
          is_locked: false,
          posted_at: new Date().toISOString(),
          created_by: _session?.user.id ?? null,
          created_at: new Date().toISOString(),
        });
        _db.journal_lines.push(
          {
            id: uuid(),
            entry_id: eid,
            account_id: expense.id,
            debit: net,
            credit: 0,
            vat_amount: 0,
            vat_rate: 0,
            description: merchant,
          },
          {
            id: uuid(),
            entry_id: eid,
            account_id: vatInput.id,
            debit: vat,
            credit: 0,
            vat_amount: vat,
            vat_rate: 14,
            description: 'VAT input',
          },
          {
            id: uuid(),
            entry_id: eid,
            account_id: cash.id,
            debit: 0,
            credit: total,
            vat_amount: 0,
            vat_rate: 0,
            description: merchant,
          },
        );
        r.journal_entry_id = eid;
      }

      save(_db);
      return { data: { ok: true, tier: 2, confidence: 0.92 }, error: null };
    }

    if (name === 'generate-burs-vat-002') {
      const { business_id, period_start, period_end } = opts?.body ?? {};
      const biz = _db.businesses.find((b) => b.id === business_id);
      if (!biz) return { data: null, error: { message: 'business not found' } };

      const vatOut = _db.accounts.find(
        (a) => a.business_id === business_id && a.gl_code === '2100',
      );
      const vatIn = _db.accounts.find((a) => a.business_id === business_id && a.gl_code === '2110');
      const fromD = new Date(period_start);
      const toD = new Date(period_end);
      const ids = new Set(
        _db.journal_entries
          .filter(
            (e) =>
              e.business_id === business_id &&
              e.is_posted &&
              new Date(e.entry_date) >= fromD &&
              new Date(e.entry_date) <= toD,
          )
          .map((e) => e.id),
      );
      let outputVat = 0,
        inputVat = 0;
      for (const l of _db.journal_lines) {
        if (!ids.has(l.entry_id)) continue;
        if (l.account_id === vatOut?.id) outputVat += l.credit - l.debit;
        if (l.account_id === vatIn?.id) inputVat += l.debit - l.credit;
      }
      const round2 = (n: number) => Math.round(n * 100) / 100;
      const result = {
        business_name: biz.name,
        vat_number: biz.vat_number,
        period_start,
        period_end,
        output_vat: round2(outputVat),
        input_vat: round2(inputVat),
        net_payable: round2(outputVat - inputVat),
        generated_at: new Date().toISOString(),
      };
      const csv = [
        'field,value',
        `business,"${biz.name}"`,
        `vat_number,${biz.vat_number ?? ''}`,
        `period_start,${period_start}`,
        `period_end,${period_end}`,
        `output_vat,${result.output_vat}`,
        `input_vat,${result.input_vat}`,
        `net_payable,${result.net_payable}`,
      ].join('\n');
      return { data: { ok: true, result, csv }, error: null };
    }

    return { data: null, error: { message: `mock function ${name} not implemented` } };
  },
};

// ============================================================
// Public mock client
// ============================================================

export const mockSupabase = {
  auth,
  storage,
  functions,
  from<T = any>(table: Table): QueryBuilder<T> {
    return new QueryBuilder<T>(table);
  },
  // helpers for debugging from the console
  _debug: {
    db: () => _db,
    reset: () => {
      localStorage.removeItem(LS_KEY);
      localStorage.removeItem(LS_SESSION);
      window.location.reload();
    },
  },
};

if (typeof window !== 'undefined') {
  (window as any).__nako_mock = mockSupabase;
}
